import React, { useState, useEffect } from 'react';
import { TierExpirationWarningModal } from './TierExpirationWarningModal';
import { useVip } from '@/contexts/VipContext';
import { useIsBrowserPromptActive } from '@/stores/browserPromptStore';

export const GlobalTierExpirationWarningModal = () => {
  const { vipTiers, userVipStatus } = useVip();
  const isBrowserPromptActive = useIsBrowserPromptActive();
  const [showWarning, setShowWarning] = useState(false);
  const [warningData, setWarningData] = useState<{
    tierName: string;
    tierColor: string;
    daysUntilExpiration: number;
    gcNeededToMaintain?: number;
  } | null>(null);

  useEffect(() => {
    if (!userVipStatus) {
      return;
    }

    // Check if we should show the warning
    // The backend sets show_tier_expiration_warning: true when:
    // - days_until_expiration <= 7
    // - last_warning_shown_at is more than 24 hours ago (or null)
    const shouldShowWarning = (userVipStatus as any).show_tier_expiration_warning === true;
    const daysUntilExpiration = (userVipStatus as any).days_until_expiration;
    const tierLevel = (userVipStatus as any).tier_level || userVipStatus.tier_level;

    // Only show for users with tier > 1 (not Member)
    if (shouldShowWarning && tierLevel > 1 && daysUntilExpiration !== null && daysUntilExpiration !== undefined) {
      // Find the current tier info
      const currentTierId = userVipStatus.current_tier_id;
      const currentTier = vipTiers.find(t => String(t.id) === String(currentTierId));
      
      // Calculate VIP points needed: requirement - current points
      const monthlyVipPoints = userVipStatus.monthly_vip_points || 0;
      const currentTierRequirement = (currentTier as any)?.points_required || currentTier?.monthly_gc_required || 0;
      const pointsNeeded = Math.max(0, currentTierRequirement - monthlyVipPoints);

      setWarningData({
        tierName: (userVipStatus as any).tier_name || currentTier?.tier_name || 'VIP',
        tierColor: (userVipStatus as any).tier_color || currentTier?.tier_color || '#FFD700',
        daysUntilExpiration: daysUntilExpiration,
        gcNeededToMaintain: pointsNeeded > 0 ? pointsNeeded : undefined,
      });
      
      // Show the modal with a small delay to ensure page is loaded
      setTimeout(() => {
        setShowWarning(true);
      }, 1000);
    }
  }, [userVipStatus, vipTiers]);

  const handleClose = () => {
    setShowWarning(false);
    setWarningData(null);
  };

  // Wait for any browser permission prompt to be dismissed first
  if (!warningData || isBrowserPromptActive) {
    return null;
  }

  return (
    <TierExpirationWarningModal
      tierName={warningData.tierName}
      tierColor={warningData.tierColor}
      daysUntilExpiration={warningData.daysUntilExpiration}
      gcNeededToMaintain={warningData.gcNeededToMaintain}
      onClose={handleClose}
      isOpen={showWarning}
    />
  );
};

